import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Badge } from '@verlo/shared/components/ui/badge';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import { Edit3, Plus, CheckCircle } from 'lucide-react';
import { OptionChips } from '@verlo/shared/components/ui/segmented';

// Pre-defined common modifiers for quick selection
const commonDrinkModifiers = [
  'Lager Top (Lemonade)',
  'Splash of Lime',
  'Dash of Blackcurrant',
  'Shandy (Half Lemonade)',
  'No Ice',
  'Extra Ice',
  'Slice of Lemon',
  'Slice of Orange',
];

const BarItemEditDialog = ({
  open,
  onOpenChange,
  itemName,
  modifiers,
  notes,
  customModifier,
  onModifiersChange,
  onNotesChange,
  onCustomModifierChange,
  onSave,
}) => {
  const toggleModifier = (mod) => {
    if (modifiers.includes(mod)) {
      onModifiersChange(modifiers.filter(m => m !== mod));
    } else {
      onModifiersChange([...modifiers, mod]);
    }
  };

  const addCustomModifier = () => {
    if (customModifier.trim() && !modifiers.includes(customModifier.trim())) {
      onModifiersChange([...modifiers, customModifier.trim()]);
      onCustomModifierChange('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="form">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 size={18} />
            Edit: {itemName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Pre-defined Drink Modifiers */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Quick Modifiers</Label>
            <OptionChips
              multiple
              value={modifiers}
              onChange={(next) => {
                const changed = next.length > modifiers.length ? next.find((m) => !modifiers.includes(m)) : modifiers.find((m) => !next.includes(m));
                if (changed) toggleModifier(changed);
              }}
              options={commonDrinkModifiers.map((mod) => ({ value: mod, label: mod, testId: `modifier-${mod.toLowerCase().replace(/\s/g, '-')}` }))}
            />
          </div>

          {/* Custom Modifier Input */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Add Custom Modifier</Label>
            <div className="flex gap-2">
              <Input
                placeholder="e.g., Double shot"
                value={customModifier}
                onChange={(e) => onCustomModifierChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCustomModifier()}
                data-testid="custom-modifier-input"
              />
              <Button
                variant="outline"
                onClick={addCustomModifier}
                disabled={!customModifier.trim()}
                data-testid="add-custom-modifier-btn"
              >
                <Plus size={16} />
              </Button>
            </div>
          </div>

          {/* Selected Modifiers Display */}
          {modifiers.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-ink-soft">Selected Modifiers</Label>
              <div className="flex flex-wrap gap-2">
                {modifiers.map((mod, idx) => (
                  <Badge
                    key={idx}
                    variant="secondary"
                    className="bg-signal-warn-tint text-signal-warn cursor-pointer hover:bg-signal-stop-tint hover:text-signal-stop transition-colors"
                    onClick={() => onModifiersChange(modifiers.filter((_, i) => i !== idx))}
                    title="Click to remove"
                  >
                    {mod} ×
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Notes Input */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Additional Notes</Label>
            <Textarea
              placeholder="Any special instructions..."
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              className="min-h-[60px]"
              data-testid="item-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="bg-verlo hover:bg-verlo-hover"
            onClick={onSave}
            data-testid="save-item-modifications-btn"
          >
            <CheckCircle size={16} className="mr-2" />
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BarItemEditDialog;
